package com.trevorwiebe.timesheet.loads.presentation.admin

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.core.presentation.common.NativeDatePicker

/**
 * Filter row for the Completed Loads screen: Employee/Customer/Carrier/Pickup/Drop chips plus Start/End date chips, matching
 * the MileageLog filter. Selections are applied client-side via [LoadsState.completedLoads], so each
 * change just routes a [LoadsEvent]. The date pickers are inline, driven by the filter state.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun CompletedLoadsFilterBar(
    state: LoadsState,
    onEvent: (LoadsEvent) -> Unit,
) {
    val filter = state.filter
    val carrierContacts = remember(state.carriers) {
        state.filterCarriers.map {
            Customer(id = it.id, contactName = it.contactName, companyName = it.companyName, phone = it.phone)
        }
    }

    Column(verticalArrangement = Arrangement.spacedBy(ChipRowGap)) {
        FlowRow(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 0.dp),
            horizontalArrangement = Arrangement.spacedBy(ChipGap),
            verticalArrangement = Arrangement.spacedBy(ChipRowGap)
        ) {
            SectionLabel("Filter")
            if (state.isElevated) {
                EmployeeFilterChip(
                    users = state.users,
                    selectedEmployeeId = filter.employeeId,
                    onSelect = { onEvent(LoadsEvent.OnFilterByEmployee(it)) }
                )
            }
            ContactFilterChip(
                label = "Customer",
                contacts = state.filterCustomers,
                selectedId = filter.customerId,
                onSelect = { onEvent(LoadsEvent.OnFilterByCustomer(it)) }
            )
            if (state.isElevated) {
                ContactFilterChip(
                    label = "Carrier",
                    contacts = carrierContacts,
                    selectedId = filter.carrierId,
                    onSelect = { onEvent(LoadsEvent.OnFilterByCarrier(it)) }
                )
            }
            LocationFilterChip(
                label = "Pickup",
                locations = state.filterLocations,
                selectedLocationId = filter.pickupLocationId,
                onSelect = { onEvent(LoadsEvent.OnFilterByPickupLocation(it)) }
            )
            LocationFilterChip(
                label = "Drop",
                locations = state.filterLocations,
                selectedLocationId = filter.dropLocationId,
                onSelect = { onEvent(LoadsEvent.OnFilterByDropLocation(it)) }
            )
        }

        FlowRow(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 0.dp),
            horizontalArrangement = Arrangement.spacedBy(ChipGap),
            verticalArrangement = Arrangement.spacedBy(ChipRowGap)
        ) {
            SectionLabel("Date")
            DateChip(
                label = "Start: ${Util.toFriendlyDate(filter.startDate)}",
                onClick = { onEvent(LoadsEvent.OnStartDateClick) },
            )
            DateChip(
                label = "End: ${Util.toFriendlyDate(filter.endDate)}",
                onClick = { onEvent(LoadsEvent.OnEndDateClick) },
            )
        }
    }

    NativeDatePicker(
        date = filter.startDate,
        showDatePicker = filter.showStartPicker,
        onDismiss = { onEvent(LoadsEvent.OnDismissStartPicker) },
        onDateSelected = { onEvent(LoadsEvent.OnStartDateSelected(it)) },
    )
    NativeDatePicker(
        date = filter.endDate,
        showDatePicker = filter.showEndPicker,
        onDismiss = { onEvent(LoadsEvent.OnDismissEndPicker) },
        onDateSelected = { onEvent(LoadsEvent.OnEndDateSelected(it)) },
    )
}

@Composable
private fun DateChip(label: String, onClick: () -> Unit) {
    FilterChip(
        selected = true,
        onClick = onClick,
        label = { Text(label) },
        border = FilterChipDefaults.filterChipBorder(
            enabled = true,
            borderColor = MaterialTheme.colorScheme.tertiary,
            selected = true
        ),
        colors = FilterChipDefaults.filterChipColors(
            containerColor = MaterialTheme.colorScheme.background,
            selectedContainerColor = MaterialTheme.colorScheme.tertiary,
            selectedLabelColor = MaterialTheme.colorScheme.onTertiary
        )
    )
}
